import React from 'react'
import { Card, Descriptions, Flex, Tag, Typography } from 'antd'
import { DescriptionsProps } from 'antd/es/descriptions'
import { useUnit } from 'effector-react'
import { Link } from 'react-router-dom'
import { GUARD_RULES } from 'src/features/session/constants'
import { $userProfile } from 'src/features/user'
import { path } from '../../../../libs/path'
import { WalletGuardWallet } from '../../types'
import {
  formatWalletDate,
  WALLET_STATUS_COLORS,
  WALLET_STATUS_LABELS,
  WALLET_TYPE_LABELS,
} from '../config'

type Props = {
  wallet: WalletGuardWallet
}

const EMPTY = '—'

export const WalletCard = ({ wallet }: Props) => {
  const type = wallet.type.toUpperCase()
  const userProfile = useUnit($userProfile)
  const canViewWallet = userProfile.permissions.includes(
    GUARD_RULES.VIEW_CRYPTO_WALLET,
  )

  const items: DescriptionsProps['items'] = [
    {
      key: 'address',
      label: 'Адрес',
      span: 2,
      children: (
        <Typography.Text copyable style={{ overflowWrap: 'anywhere' }}>
          {wallet.address}
        </Typography.Text>
      ),
    },
    {
      key: 'name',
      label: 'Название',
      children: wallet.name || EMPTY,
    },
    {
      key: 'type',
      label: 'Тип',
      children: WALLET_TYPE_LABELS[type] ?? wallet.type,
    },
    {
      key: 'currency',
      label: 'Валюта',
      children: wallet.currency || EMPTY,
    },
    {
      key: 'network',
      label: 'Сеть',
      children: wallet.network || EMPTY,
    },
    {
      key: 'status',
      label: 'Статус',
      children: (
        <Tag color={WALLET_STATUS_COLORS[wallet.status]}>
          {WALLET_STATUS_LABELS[wallet.status] ?? wallet.status}
        </Tag>
      ),
    },
    {
      key: 'checkStatus',
      label: 'Статус проверки',
      children: wallet.checkStatus || EMPTY,
    },
    {
      key: 'amlScore',
      label: 'AML-оценка',
      children: wallet.amlScore ?? EMPTY,
    },
    {
      key: 'riskFlags',
      label: 'Риск-флаги',
      span: 2,
      children: wallet.riskFlags?.length ? (
        <Flex gap={4} wrap="wrap">
          {wallet.riskFlags.map((riskFlag) => (
            <Tag key={riskFlag.code} style={{ marginInlineEnd: 0 }}>
              {riskFlag.label || riskFlag.code}
            </Tag>
          ))}
        </Flex>
      ) : (
        EMPTY
      ),
    },
    {
      key: 'paymentSystems',
      label: 'Платёжные системы',
      span: 2,
      children: wallet.paymentSystems?.length ? (
        <Flex gap={4} wrap="wrap">
          {wallet.paymentSystems.map((paymentSystem) => (
            <Tag key={paymentSystem} style={{ marginInlineEnd: 0 }}>
              {paymentSystem}
            </Tag>
          ))}
        </Flex>
      ) : (
        EMPTY
      ),
    },
    // Финальный кошелёк задаётся при создании только для транзитных.
    ...(type === 'TRANSIT'
      ? [
          {
            key: 'finalWallet',
            label: 'Финальный кошелёк',
            span: 2,
            children:
              wallet.finalWalletId === null ? (
                EMPTY
              ) : canViewWallet ? (
                <Link
                  to={path.walletGuard.walletDetails(
                    String(wallet.finalWalletId),
                  )}
                >
                  {`#${wallet.finalWalletId}`}
                </Link>
              ) : (
                `#${wallet.finalWalletId}`
              ),
          },
        ]
      : []),
    {
      key: 'comment',
      label: 'Комментарий',
      span: 2,
      children: wallet.comment || EMPTY,
    },
    {
      key: 'createdAt',
      label: 'Дата создания',
      span: 2,
      children: formatWalletDate(wallet.createdAt),
    },
    {
      key: 'lastCheckedAt',
      label: 'Дата последней проверки',
      span: 2,
      children: formatWalletDate(wallet.lastCheckedAt),
    },
  ]

  return (
    <Flex vertical gap={24}>
      <Card size="small" title="Данные кошелька">
        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 1, md: 2 }}
          items={items}
        />
      </Card>

      {type !== 'OWN' && (
        <Card size="small" title="Текущий баланс">
          <Typography.Text
            style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}
          >
            {wallet.balance === null
              ? EMPTY
              : `${wallet.balance} ${wallet.currency}`}
          </Typography.Text>
        </Card>
      )}
    </Flex>
  )
}
